import * as Sentry from "@sentry/react";

import bbox from "@turf/bbox";
import {
  Feature,
  GeometryCollection,
  LineString,
  Point,
  featureCollection,
  geometryCollection,
  point
} from "@turf/helpers";
import axios from "axios";
import { DATA_SVC_RS_ADDRESS, EMPTY_FILTER_ID } from "constants/app.constants";
import {
  HZ_RASTER_MVT_CROSSOVER_ZOOM,
  STICK_LAYER,
  WELL_LABEL_LAYER,
  WELL_LAYER,
  WELL_LAYER_POINT,
  XDA_INTERCEPT_LAYER,
  XDA_LABEL
} from "constants/mapLayers.constants";
import { XDA_LINE_LABEL_A, XDA_LINE_LABEL_A_PRIME } from "constants/xdaBin.constants";
import mapboxgl, { GeoJSONSource } from "mapbox-gl";
import { PadScenario } from "store/features";
import type { ISelectedWell } from "stores/map";

import { getUwisInFilter } from "api/filter";

import { absolutizeServiceRoot } from "./absolutizeServiceRoot";

const mapServiceEndpoint = absolutizeServiceRoot(process.env.REACT_APP_MAP_SERVICE);
const dataServiceRustEndpoint = `${DATA_SVC_RS_ADDRESS}/api/v1/data`;

export interface FeatureIntersectionResult {
  selectedWells: { [name: string]: ISelectedWell };
  selectedFeatures: GeoJSON.Feature[];
  wellsIntersect: { [uwi: string]: string };
}

export interface Point2 {
  x: number;
  y: number;
}
export interface Line {
  start: Point2;
  end: Point2;
}

/**
 * Write the A / A' pair for a section line. Every path that paints the line
 * calls this, because a line drawn without it is an unlabelled stroke across
 * the map — which is what the card's derived line was.
 *
 * A is the end clicked first, wherever it sits on the map. Turning that into a
 * left and a right end is the viewer's job, not the map's: the viewer always
 * draws the western end on the left, and calls that end A' when the line runs
 * east to west (`selectIsXdaLineReversed`). Swapping the ends here as well
 * would put the map's A on the opposite end of the section from the viewer's.
 */
export function setXdaLineLabels(mapbox: mapboxgl.Map, line: Line): void {
  if (!(line && line.start && line.end) || !mapbox) {
    return;
  }
  const labelSource = mapbox.getSource(XDA_LABEL) as GeoJSONSource;
  if (!labelSource) {
    return;
  }
  labelSource.setData(
    featureCollection([
      point([line.start.x, line.start.y], { label: XDA_LINE_LABEL_A }),
      point([line.end.x, line.end.y], { label: XDA_LINE_LABEL_A_PRIME })
    ])
  );
}

export function setXdaLine(
  line: Line,
  interceptPoints: Point2[],
  mapbox: mapboxgl.Map
): Feature<GeometryCollection> {
  if (!(line && line.start && line.end) || !mapbox) {
    return;
  }
  const ls: LineString = {
    type: "LineString",
    coordinates: [
      [line.start.x, line.start.y],
      [line.end.x, line.end.y]
    ]
  };

  const geoms: (Point | LineString)[] = interceptPoints.map((p) => {
    const point: Point = { type: "Point", coordinates: [p.x, p.y] };
    return point;
  });
  geoms.push(ls);
  const gcoll = geometryCollection(geoms);
  const source = mapbox.getSource(XDA_INTERCEPT_LAYER) as GeoJSONSource;
  if (!source) {
    return;
  }

  source.setData(gcoll);
  setXdaLineLabels(mapbox, line);
  return gcoll;
}

export interface TileUris {
  hz: string;
  v: string;
  facility: string;
  hzRaster: string;
}

/**
 * The client-side default for `wellLineThickness` / `wellDotThickness` (see the
 * map store's initial display settings). A tile URI only carries a thickness
 * param when the user has moved OFF this value — see `appendRasterThickness`.
 */
export const DEFAULT_WELL_THICKNESS = 2;

const RASTER_LINE_THICKNESS_PARAM = "lineThickness";
const RASTER_DOT_THICKNESS_PARAM = "dotThickness";
const RASTER_THICKNESS_PARAMS = [
  RASTER_LINE_THICKNESS_PARAM,
  RASTER_DOT_THICKNESS_PARAM
] as const;

/**
 * Appends the user's well-thickness settings to a raster tile URI (EVA-231).
 *
 * Below the raster/MVT crossover the well layers this app paints with Mapbox
 * paint properties are switched off, so `wellLineThickness`/`wellDotThickness`
 * have nothing to act on — the raster is drawn server-side. Carrying the two
 * values on the tile URI is the only way the setting can reach those pixels.
 *
 * A value still sitting at `DEFAULT_WELL_THICKNESS` is deliberately OMITTED
 * rather than sent explicitly. eva-map-svc treats an absent param as the
 * default, so the URI every user who never touches the setting requests stays
 * byte-identical to the pre-EVA-231 one — no cache churn, and no re-fetch of
 * tiles that would come back identical.
 */
export function appendRasterThickness(
  uri: string,
  lineThickness: Maybe<number>,
  dotThickness: Maybe<number>
): string {
  // Preserve the falsy sentinel that tears down the raster source/layer, for the
  // same reason computeHzTileUri does below.
  if (!uri) {
    return uri;
  }

  const settings: [string, Maybe<number>][] = [
    [RASTER_LINE_THICKNESS_PARAM, lineThickness],
    [RASTER_DOT_THICKNESS_PARAM, dotThickness]
  ];

  const params = settings
    // Number.isFinite, not `typeof value === "number"` (PR #6625 review): the stepper
    // writes parseFloat(...) into the store with no guard, so CLEARING the input puts
    // NaN there. `typeof NaN === "number"` is true and `NaN !== 2` is true, so the old
    // check shipped `?lineThickness=NaN` — which changes the URI, so the effect fires
    // setTiles(), clearing the tile cache and re-fetching every visible raster tile for
    // pixels that come back identical (eva-map-svc maps a non-finite value back to the
    // default). The vector path already guards the same case with `> 0` in
    // applyWellStyles. Infinities go the same way. NEGATIVES deliberately do not:
    // the stepper clamps only its upper bound, so a negative can reach the store,
    // and eva-map-svc clamps it to the minimum thickness — a real, different render.
    // Omitting it here would instead render the DEFAULT, so the two ends would
    // disagree. Range clamping has one owner, and it is the server.
    .filter(([, value]) => Number.isFinite(value) && value !== DEFAULT_WELL_THICKNESS)
    .map(([name, value]) => `${name}=${value}`);

  if (params.length === 0) {
    return uri;
  }

  return `${uri}${uri.includes("?") ? "&" : "?"}${params.join("&")}`;
}

/**
 * Removes any thickness params `appendRasterThickness` added, leaving the rest
 * of the URI (including `scenarioId`) untouched.
 *
 * Exists so the thickness can be re-derived from a live tile URI without
 * threading a second "base" copy of it through every construction site — strip,
 * then re-append. Keeping the two operations adjacent is what stops them
 * drifting on which params they own.
 */
export function stripRasterThickness(uri: string): string {
  const at = uri.indexOf("?");
  if (at === -1) {
    return uri;
  }

  // Deliberately NOT URLSearchParams (PR #6625 review). It would express the
  // filtering more directly, but its round trip does not preserve the params
  // this function does not own: `toString()` re-encodes `:` to `%3A` and
  // rewrites `%20` to `+`. Scenario ids reach this URI raw (computeHzTileUri
  // appends them unencoded) and can contain a colon — eva-map-svc's own tests
  // use ids shaped like `5:hello`. Re-encoding them yields a URL that is
  // semantically identical but textually different, which is exactly what
  // hasTileUriChanged keys on, so every first thickness change would also
  // trigger a needless raster re-fetch. Splitting on the first `?` and
  // filtering whole `name=` params leaves everything else byte-for-byte.
  const base = uri.slice(0, at);
  const kept = uri
    .slice(at + 1)
    .split("&")
    .filter(
      (param) => !RASTER_THICKNESS_PARAMS.some((name) => param.startsWith(`${name}=`))
    );

  return kept.length > 0 ? `${base}?${kept.join("&")}` : base;
}

export function computeHzTileUri(
  hzTileUri: string,
  // `padScenarios` is typed as required, so this default only fires for callers
  // that omit the arg (or untyped/JS callers — `strict: false` won't flag them).
  // Every current call site passes scenarios explicitly, so nothing relies on it
  // today; it is defensive only, keeping an omitted arg from throwing on
  // `.filter` below rather than being load-bearing for any specific caller.
  padScenarios: PadScenario[] = []
): string {
  // An empty input URI must come back empty. Callers use a falsy `hzRaster` as
  // the sentinel that tears down the raster source/layer (`if (uri.hzRaster)` in
  // useMapTileSourceManager); returning a truthy "?scenarioId=..." here would
  // stand up a raster source pointing at nothing. Keep this structural guard —
  // do not "simplify" it away on the assumption that call sites always pre-check.
  if (!hzTileUri) {
    return hzTileUri;
  }
  const scenariosWithId = padScenarios.filter((s) => s.scenarioId);
  if (scenariosWithId.length === 0) {
    return hzTileUri;
  }
  // Use includes("?") to pick the correct query separator for the first param.
  // Subsequent scenarioIds use "&" as the separator.
  const firstSeparator = hzTileUri.includes("?") ? "&" : "?";
  const [first, ...rest] = scenariosWithId;
  const params =
    `${firstSeparator}scenarioId=${encodeURIComponent(first.scenarioId)}` +
    rest.map((s) => `&scenarioId=${encodeURIComponent(s.scenarioId)}`).join("");
  return `${hzTileUri}${params}`;
}

export function getTileUris(
  mapServiceEndpoint: string,
  orgId: string,
  padScenarios: PadScenario[] = []
): TileUris {
  const hzTileUri = `${mapServiceEndpoint}/well/hz/${orgId}/{z}/{x}/{y}`;
  const veritcalTileUri = `${mapServiceEndpoint}/well/vert/${orgId}/{z}/{x}/{y}`;
  const facilityTileUri = `${mapServiceEndpoint}/well/facility/${orgId}/{z}/{x}/{y}`;

  return {
    hz: computeHzTileUri(hzTileUri, padScenarios),
    v: veritcalTileUri,
    facility: facilityTileUri,
    hzRaster: ""
  };
}

export async function getFeaturesIntersectingPolygon(
  mapbox: mapboxgl.Map,
  polygon: GeoJSON.Geometry | GeoJSON.Feature,
  filterToRenderedWells = true,
  filterId?: string
): Promise<FeatureIntersectionResult> {
  if (!mapbox) {
    return {
      selectedWells: null,
      selectedFeatures: null,
      wellsIntersect: null
    };
  }
  if (!mapbox.getLayer(WELL_LAYER)) {
    return {
      selectedWells: null,
      selectedFeatures: null,
      wellsIntersect: null
    };
  }
  const bounding = bbox(polygon);
  const p1 = mapbox.project({
    lng: bounding[0],
    lat: bounding[1]
  });
  const p2 = mapbox.project({
    lng: bounding[2],
    lat: bounding[3]
  });
  //polygon can be a feature or geometry
  let geom = polygon;
  if (polygon.type === "Feature" && polygon.geometry) {
    geom = polygon.geometry;
  }
  if (!mapbox.getLayer(WELL_LAYER)) {
    return {
      selectedWells: null,
      selectedFeatures: null,
      wellsIntersect: null
    };
  }
  // Build the queried layer list dynamically — only include layers that exist.
  // mapbox-gl's Style#queryRenderedFeatures fires an ErrorEvent and returns []
  // for the ENTIRE query on the first layer id that isn't in the style, so one
  // absent layer would silently empty the whole selection rather than just
  // dropping that layer's features. Sticks in particular can be missing while
  // Wells exists (style reload — the same window the WELL_LAYER guard above
  // covers), which would make every lasso at zoom >= 8 come back with nothing.
  const queryLayers: string[] = [WELL_LAYER];
  if (mapbox.getLayer(WELL_LAYER_POINT)) {
    queryLayers.push(WELL_LAYER_POINT);
  }
  if (mapbox.getLayer(STICK_LAYER)) {
    queryLayers.push(STICK_LAYER);
  }
  const features = mapbox.queryRenderedFeatures([p1, p2], {
    layers: queryLayers
  });
  const stickFeatures = features.filter((f) => f.properties?.group == "stick");

  let wellsIntersect: { [uwi: string]: string } = null;
  try {
    const response = await axios.post<{ [uwi: string]: string }>(
      `${mapServiceEndpoint}/map/wells-intersect`,
      geom
    );

    if (response.status !== 200) {
      return {
        selectedWells: null,
        selectedFeatures: null,
        wellsIntersect: null
      };
    }
    wellsIntersect = response.data;
  } catch (err) {
    return {
      selectedWells: null,
      selectedFeatures: null,
      wellsIntersect: null
    };
  }

  let filteredWellsIntersect = wellsIntersect;
  if (filterToRenderedWells) {
    // Branch on the configured crossover zoom, never on `features.length > 0`:
    // rendered-feature results can be empty while vector tiles are still
    // loading above the crossover, so emptiness does not mean "raster is live".
    // Note the converse case is no longer reachable — this PR gates STICK_LAYER
    // to zoom [8, 24], so nothing can render below the crossover while the well
    // layers are gated off (the original motivating bug: a stick returned at
    // zoom 6). The test covering that direction is therefore a synthetic guard
    // against reintroducing a features.length proxy, not a reproduction of a
    // state the app can currently reach.
    const isRasterZoom = (mapbox.getZoom?.() ?? Infinity) < HZ_RASTER_MVT_CROSSOVER_ZOOM;
    if (!isRasterZoom) {
      // Narrow to wells actually rendered on the map (EVA-6875 client-side backstop).
      const renderedUwis = new Set<string>();
      for (const feat of features) {
        if (feat.properties?.Uwi) renderedUwis.add(feat.properties.Uwi);
        if (feat.properties?.legUwi) renderedUwis.add(feat.properties.legUwi);
      }
      filteredWellsIntersect = {};
      for (const uwi in wellsIntersect) {
        if (renderedUwis.has(uwi)) filteredWellsIntersect[uwi] = wellsIntersect[uwi];
      }
    } else {
      // ponytail: raster serves zoom < 8, so queryRenderedFeatures returns nothing
      // to narrow against. Ask the filter service instead.
      // Initialise to {} unconditionally: every path through this branch must
      // leave an object (never null), otherwise a downstream
      // filteredWellsIntersect[feat.properties.Uwi] lookup on a stick feature
      // throws when /map/wells-intersect returns a 200 with a null body and
      // hasActiveFilter is true but uwisToCheck is empty.
      filteredWellsIntersect = {};
      const uwisToCheck = Object.keys(wellsIntersect ?? {});
      // EMPTY_FILTER_ID is a truthy sentinel for "no filter applied" — treat it
      // the same as a missing filterId so it never gets POSTed to /contains as
      // if it were a real filter id.
      const hasActiveFilter = !!filterId && filterId !== EMPTY_FILTER_ID;
      // No active filter means nothing to scope to, and it also means nothing is
      // rendered: useWellMapUpdater's EMPTY_MAP_RESULT handling kicks in, tiles key
      // on CLEAR_MAP_ID, and zoom >= 8 already narrows to {} in that state. So the
      // {} above stands — passing every intersecting well through unfiltered would
      // contradict what's on screen. Never POST to /contains without a real filter id.
      if (hasActiveFilter && uwisToCheck.length > 0) {
        try {
          const { status, data } = await getUwisInFilter(filterId, uwisToCheck);
          if (status !== 200 || !Array.isArray(data)) {
            throw new Error("Unexpected response from /contains");
          }
          const inFilter = new Set(data);
          for (const uwi of uwisToCheck) {
            if (inFilter.has(uwi)) filteredWellsIntersect[uwi] = wellsIntersect[uwi];
          }
        } catch (err) {
          // The Rust handler documents that an internal /contains failure returns
          // 400 and never a 200-with-empty-array, so a caught error here is a
          // genuine, distinguishable server failure worth surfacing — capture it
          // rather than silently swallowing it. The {} fallback below is
          // unchanged: callers must never see an unfiltered wellsIntersect.
          Sentry.withScope((scope) => {
            scope.setContext("getFeaturesIntersectingPolygon", {
              action: "getUwisInFilter",
              filterId,
              uwiCount: uwisToCheck.length
            });
            scope.setLevel("error");
            Sentry.captureException(err);
          });
          filteredWellsIntersect = {};
        }
      }
    }
  }

  let wellsStatus: { [uwi: string]: string } = {};
  let wellsGroupName: { [uwi: string]: string } = {};
  let wellTypes: { [uwi: string]: string } = {};
  // Skip entirely when nothing is rendered: with features.length === 0,
  // selectedFeatures below is always [], so selectedWells is always {}, and every
  // consumer of wellsStatus/wellsGroupName/wellTypes is gated on `well in
  // selectedWells` or on iterating selectedFeatures — nothing downstream can ever
  // read this response, so firing it would just be an unbounded, useless request
  // (this is the path LassoSelection.tsx always hits, since it passes no filterId).
  const uwisToFetch = features.length > 0 ? Object.keys(filteredWellsIntersect) : [];
  if (uwisToFetch.length > 0) {
    try {
      const response = await axios.post<{ [uwi: string]: string[] }>(
        `${dataServiceRustEndpoint}/from-well-list`,
        {
          uwis: uwisToFetch,
          fields: ["Header.Status", "Header.GroupName", "Header.WellType"]
        }
      );

      if (response.status == 200) {
        wellsStatus = Object.fromEntries(
          Object.entries(response.data).map(([uwi, values]) => [uwi, values[0]])
        );
        wellsGroupName = Object.fromEntries(
          Object.entries(response.data).map(([uwi, values]) => [uwi, values[1]])
        );
        wellTypes = Object.fromEntries(
          Object.entries(response.data).map(([uwi, values]) => [uwi, values[2]])
        );
      }
    } catch (err) {
      // Ignore error—well status will not be available
    }
  }

  const selectedFeatures = [];
  for (const feat of features) {
    if (
      (feat.properties?.group != "leg" && filteredWellsIntersect[feat.properties.Uwi]) ||
      (feat.properties?.group == "leg" &&
        filteredWellsIntersect[feat.properties.Uwi] &&
        filteredWellsIntersect[feat.properties?.legUwi])
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      selectedFeatures.push((feat as any).toJSON());
    }
  }

  for (const feat of stickFeatures) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedFeatures.push((feat as any).toJSON());
  }

  const selectedWells = {};
  const colorByGroupNames = {};
  for (const feat of selectedFeatures) {
    if (!(feat.properties.legUwi in selectedWells) && feat.properties?.group == "leg") {
      selectedWells[feat.properties.legUwi] = {
        ...feat.properties,
        Uwi: feat.properties.legUwi,
        // Preserve the parent well's UWI — the filter endpoint never returns the
        // synthetic legUwi, so consumers recolour the leg from the parent.
        parentUwi: feat.properties.Uwi,
        color: feat?.state?.color
      };
      continue;
    }
    selectedWells[feat.properties.Uwi] = {
      Uwi: feat.properties.Uwi,
      color: feat?.state?.color
    };

    if (feat.properties.Uwi in wellsGroupName) {
      colorByGroupNames[wellsGroupName[feat.properties.Uwi]] = feat?.state?.color;
    }
  }

  // Add SAGD injection wells if any SAGD production wells are selected
  const hasSAGDPRODWell = Object.keys(wellsStatus).some(
    (well) =>
      wellsStatus[well] === "06. PROD" &&
      wellTypes[well] === "SAGD" &&
      well in selectedWells
  );

  if (hasSAGDPRODWell) {
    for (const well in wellsStatus) {
      const groupName = wellsGroupName[well];
      const isValidGroupName = groupName?.toLowerCase() !== "subscription required";

      // Check if this is an unselected SAGD injection well with a matching PROD well
      if (
        wellsStatus[well] === "06. INJ" &&
        wellTypes[well] === "SAGD" &&
        !(well in selectedWells) &&
        isValidGroupName
      ) {
        const matchingProdWell = Object.keys(wellsGroupName).find(
          (otherWell) =>
            wellsGroupName[otherWell] === groupName &&
            wellsStatus[otherWell] === "06. PROD" &&
            otherWell in selectedWells
        );
        if (matchingProdWell) {
          selectedWells[well] = {
            Uwi: well,
            group: "inj",
            // Mirror the matching PROD well — its group colour is what the inj
            // well inherits, and the filter endpoint won't return the inj uwi.
            parentUwi: matchingProdWell,
            color: colorByGroupNames[groupName] || "#212121"
          };
        }
      }
    }
  }

  const uwis = Object.keys(selectedWells);
  const labelFeatures = mapbox.querySourceFeatures(WELL_LABEL_LAYER, {
    sourceLayer: "eva-wells-label",
    filter: ["in", ["get", "Uwi"], ["literal", uwis]]
  });
  const labelDict = {};
  for (const label of labelFeatures) {
    if (!labelDict[label.properties.Uwi]) {
      labelDict[label.properties.Uwi] = label.properties.value;
    }
  }
  for (const uwi of Object.keys(selectedWells)) {
    if (labelDict[uwi]) {
      selectedWells[uwi]["value"] = labelDict[uwi];
    }
  }
  return {
    selectedWells,
    selectedFeatures,
    wellsIntersect: filteredWellsIntersect
  };
}
